import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Gift, Plus, Search, Filter, MapPin, Clock, Heart, Eye,
  Star, Sparkles, Users, Compass, BookOpen, Palette, Briefcase,
  MessageCircle, RefreshCw, Loader2, ChevronRight, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { HeartAlchemyIcon, LotusIcon, SpiralIcon, CrystalIcon } from './icons/AlchemyIcons';
import OfferingCard from './OfferingCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { value: 'all', label: 'All Gifts', icon: Gift },
  { value: 'coaching', label: 'Coaching', icon: Compass },
  { value: 'mentoring', label: 'Mentoring', icon: Users },
  { value: 'healing', label: 'Healing', icon: HeartAlchemyIcon },
  { value: 'teaching', label: 'Teaching', icon: BookOpen },
  { value: 'creative', label: 'Creative', icon: Palette },
  { value: 'professional', label: 'Professional', icon: Briefcase },
  { value: 'spiritual', label: 'Spiritual', icon: LotusIcon },
  { value: 'nvc', label: 'NVC', icon: MessageCircle },
  { value: 'other', label: 'Other', icon: Gift }
];

const OFFERING_TYPES = [
  { value: 'service', label: 'Service' },
  { value: 'knowledge', label: 'Knowledge / Teaching' },
  { value: 'time', label: 'Time / Attention' },
  { value: 'skill', label: 'Skill / Expertise' },
  { value: 'item', label: 'Physical Item' }
];

const AVAILABILITY_OPTIONS = [
  { value: 'ongoing', label: 'Ongoing - Available regularly' },
  { value: 'limited', label: 'Limited - While supplies/time last' },
  { value: 'one-time', label: 'One-time - Single offering' }
];

const LOCATION_OPTIONS = [
  { value: 'remote', label: '🌐 Remote / Online' },
  { value: 'local', label: '📍 Local / In-person' },
  { value: 'hybrid', label: '🔄 Hybrid - Both options' }
];

export default function CommunityOfferings() {
  const navigate = useNavigate();
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categoryStats, setCategoryStats] = useState({});
  
  const [newOffering, setNewOffering] = useState({
    title: '',
    description: '',
    category: 'coaching',
    offering_type: 'service',
    availability: 'ongoing',
    location: 'remote',
    area: '',
    tags: ''
  });
  
  const userId = localStorage.getItem('workbook_user_id');
  const userName = localStorage.getItem('workbook_user_name');

  useEffect(() => {
    fetchOfferings();
  }, [selectedCategory, searchQuery]);

  const fetchOfferings = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/offerings?limit=20`;
      if (selectedCategory !== 'all') url += `&category=${selectedCategory}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setOfferings(data.offerings || []);
        setCategoryStats(data.category_stats || {});
      }
    } catch (error) {
      console.error('Failed to fetch offerings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffering = async () => {
    if (!userId) {
      toast.error('Please log in to share a gift');
      navigate('/login');
      return;
    }

    if (!newOffering.title.trim() || !newOffering.description.trim()) {
      toast.error('Please fill in the title and description');
      return;
    }

    setCreating(true);
    try {
      const tags = newOffering.tags
        .split(',')
        .map(t => t.trim().toLowerCase())
        .filter(t => t.length > 0);

      const response = await fetch(`${BACKEND_URL}/api/offerings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
          'X-User-Name': userName
        },
        body: JSON.stringify({
          ...newOffering,
          tags
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('🎁 Your gift has been shared with the community!');
        setShowCreateDialog(false);
        setNewOffering({
          title: '',
          description: '',
          category: 'coaching',
          offering_type: 'service',
          availability: 'ongoing',
          location: 'remote',
          area: '',
          tags: ''
        });
        fetchOfferings();
        
        // Navigate to the new offering page
        navigate(`/offerings/${data.offering_id}`);
      } else {
        throw new Error('Failed to create');
      }
    } catch (error) {
      toast.error('Failed to share your gift');
    } finally {
      setCreating(false);
    }
  };

  const totalOfferings = Object.values(categoryStats).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-light text-white flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Gift className="w-6 h-6 text-amber-400" />
            Community Gifts
          </h2>
          <p className="text-stone-500 text-sm mt-1">
            Freely offered services, skills, and knowledge from our community
          </p>
        </div>
        
        {userId && (
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white"
            data-testid="create-offering-btn"
          >
            <Gift className="w-4 h-4 mr-2" />
            Share a Gift
          </Button>
        )}
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-amber-500/10 to-rose-500/10 rounded-lg border border-amber-500/20">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <span className="text-amber-300 font-medium">{totalOfferings}</span>
          <span className="text-stone-400 text-sm">gifts available</span>
        </div>
        <div className="h-4 w-px bg-stone-700" />
        <p className="text-xs text-stone-500">
          A gift economy built on generosity and trust
        </p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search gifts..."
            className="pl-10 bg-stone-800/50 border-stone-700 text-white"
          />
        </div>
        
        <ScrollArea className="w-full sm:w-auto">
          <div className="flex gap-2 pb-2">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const count = cat.value === 'all' ? totalOfferings : (categoryStats[cat.value] || 0);
              return (
                <Button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  variant={selectedCategory === cat.value ? 'default' : 'outline'}
                  size="sm"
                  className={`whitespace-nowrap ${
                    selectedCategory === cat.value 
                      ? 'bg-amber-600 text-white' 
                      : 'border-stone-700 text-stone-400 hover:text-white hover:border-stone-600'
                  }`}
                >
                  <Icon className="w-3 h-3 mr-1" />
                  {cat.label}
                  {count > 0 && (
                    <span className="ml-1 text-xs opacity-70">({count})</span>
                  )}
                </Button>
              );
            })}
          </div>
        </ScrollArea>
      </div>

      {/* Offerings Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      ) : offerings.length === 0 ? (
        <div className="text-center py-12 bg-stone-900/30 rounded-lg border border-stone-800">
          <Gift className="w-12 h-12 mx-auto text-stone-600 mb-4" />
          <p className="text-stone-400 mb-2">No gifts found</p>
          <p className="text-stone-500 text-sm mb-4">Be the first to share a gift with the community!</p>
          {userId && (
            <Button
              onClick={() => setShowCreateDialog(true)}
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Share Your Gift
            </Button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {offerings.map(offering => (
            <OfferingCard 
              key={offering._id} 
              offering={offering}
              onShare={() => toast.success('Link copied to clipboard!')}
            />
          ))}
        </div>
      )}

      {/* Create Offering Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-light flex items-center gap-3 text-amber-300" style={{ fontFamily: 'Playfair Display, serif' }}>
              <Gift className="w-6 h-6" />
              Share a Gift with the Community
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            {/* Intro Text */}
            <div className="p-4 bg-gradient-to-r from-amber-500/10 to-rose-500/10 rounded-lg border border-amber-500/20">
              <p className="text-sm text-stone-300">
                💚 Share your gifts freely with the community. No strings attached — just the joy of giving and building trust through generosity.
              </p>
            </div>
            
            {/* Title */}
            <div>
              <Label className="text-stone-300 text-sm mb-2 block">
                What are you offering? <span className="text-rose-400">*</span>
              </Label>
              <Input
                value={newOffering.title}
                onChange={(e) => setNewOffering({...newOffering, title: e.target.value})}
                placeholder="e.g., NVC Practice Session, Career Mentoring, Healing Session..."
                className="bg-stone-800 border-stone-700 text-white"
                data-testid="offering-title-input"
              />
            </div>
            
            {/* Description */}
            <div>
              <Label className="text-stone-300 text-sm mb-2 block">
                Describe your gift <span className="text-rose-400">*</span>
              </Label>
              <Textarea
                value={newOffering.description}
                onChange={(e) => setNewOffering({...newOffering, description: e.target.value})}
                placeholder="Share what you're offering, who might benefit, and what to expect..."
                className="bg-stone-800 border-stone-700 text-white min-h-[120px]"
                data-testid="offering-description-input"
              />
            </div>
            
            {/* Category & Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">Category</Label>
                <Select 
                  value={newOffering.category} 
                  onValueChange={(v) => setNewOffering({...newOffering, category: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                      <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-stone-700">
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">Type</Label>
                <Select 
                  value={newOffering.offering_type} 
                  onValueChange={(v) => setNewOffering({...newOffering, offering_type: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {OFFERING_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white hover:bg-stone-700">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Availability & Location */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">Availability</Label>
                <Select 
                  value={newOffering.availability} 
                  onValueChange={(v) => setNewOffering({...newOffering, availability: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {AVAILABILITY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-stone-700">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">Location</Label>
                <Select 
                  value={newOffering.location} 
                  onValueChange={(v) => setNewOffering({...newOffering, location: v})}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-700">
                    {LOCATION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-stone-700">
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Area (if local) */}
            {(newOffering.location === 'local' || newOffering.location === 'hybrid') && (
              <div>
                <Label className="text-stone-300 text-sm mb-2 block">Area / City</Label>
                <Input
                  value={newOffering.area}
                  onChange={(e) => setNewOffering({...newOffering, area: e.target.value})}
                  placeholder="e.g., San Francisco Bay Area"
                  className="bg-stone-800 border-stone-700 text-white"
                />
              </div>
            )}
            
            {/* Tags */}
            <div>
              <Label className="text-stone-300 text-sm mb-2 block">Tags (comma-separated)</Label>
              <Input
                value={newOffering.tags}
                onChange={(e) => setNewOffering({...newOffering, tags: e.target.value})}
                placeholder="e.g., empathy, communication, healing, beginners"
                className="bg-stone-800 border-stone-700 text-white"
              />
            </div>
            
            {/* Submit */}
            <Button
              onClick={handleCreateOffering}
              disabled={creating || !newOffering.title.trim() || !newOffering.description.trim()}
              className="w-full bg-gradient-to-r from-amber-600 to-rose-600 hover:from-amber-500 hover:to-rose-500 text-white"
              data-testid="submit-offering-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sharing Your Gift...
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Share This Gift
                </>
              )}
            </Button>
            
            {/* Note */}
            <p className="text-xs text-stone-500 text-center">
              By sharing, you're contributing to a gift economy built on trust and generosity.
              Your credibility grows with each gift you share. 💚
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
